import { useFormatter, useTranslations } from 'use-intl';
import Icon, { Icons } from '@/components/elements/Icon';
import Collapsible from '@/components/modules/Collapsible';
import FormattedEther from '@/components/elements/FormattedEther/FormattedEther';
import {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  ReactNode,
  Ref,
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Asset, Vault } from 'nftx.js';
import Price from '../../elements/Price';

export interface AuctionsProps {
  items?: Asset[];
  totalEthGain?: bigint;
  totalSales?: number;
  isLoading: boolean;
  blockNumber?: bigint;
  vaults: Vault[] | undefined;
  selectedVault: Vault | undefined;
  onVaultSelect: (vaultId: string) => void;
  children: ReactNode;
  sortDir: 'newest' | 'oldest';
  setSortDir: Dispatch<SetStateAction<'newest' | 'oldest'>>;
}

export const Auctions = ({
  items = [],
  isLoading,
  totalEthGain,
  totalSales,
  blockNumber,
  onVaultSelect,
  selectedVault,
  vaults,
  children,
  sortDir,
  setSortDir,
}: AuctionsProps) => {
  const t = useTranslations('Auctions');
  const { relativeTime } = useFormatter();
  const [updated, setUpdated] = useState(Date.now());
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // update when block comes in
    setUpdated(Date.now());
    setNow(Date.now());

    // update every second
    const intervalId = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [blockNumber]);

  const selectCollectionRef = useRef<HTMLSelectElement>(null);
  const sortRef = useRef<HTMLSelectElement>(null);

  const handleSelectCollectionChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onVaultSelect(e.target.value);
  };

  const handleSortChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSortDir(e.target.value as 'newest' | 'oldest');
  };

  const handleClick = (ref: RefObject<HTMLSelectElement>) => {
    if (ref?.current) {
      ref.current.classList.remove('opacity-0');
      ref.current.focus();
    }
  };

  const stats = [
    ['sales', totalSales ?? '—'],
    [
      'received',
      totalEthGain ? <Price currency="ETH" value={totalEthGain} /> : '—',
    ],
  ] as const;

  return (
    <article className="space-y-8">
      <header className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-center">
        <hgroup className="xl:col-span-2">
          <h1 className="font-bold text-2xl">{t('title')}</h1>
          <h2 className="font-medium text-gray-500">{t('subtitle')}</h2>
        </hgroup>
        <aside className="px-6 py-4 bg-gray-50 rounded-md">
          <dl className="flex-1 rounded-md bg-gray-50 justify-around flex flex-col sm:flex-row text-center items-center space-x-4">
            {stats.map(([key, val]) => (
              <div key={key}>
                <dt className="bg-gray-200/70 py-1 px-2 uppercase text-xs text-gray-500 font-medium mb-1 rounded-sm">
                  {t(key)}
                </dt>
                <dd className="font-black text-xl">{val}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </header>

      <aside>
        <Collapsible
          gray={50}
          header={
            <h3 className="flex items-center font-medium text-base">
              <Icon
                name={Icons.QUESTION_MARK_CIRCLE}
                size={20}
                className="mr-2 text-pink-500"
              />
              {t.rich('what')}
            </h3>
          }
        >
          <div className="overflow-hidden p-4 lg:p-6 space-y-4">
            <p className="text-gray-500 text-base ">
              {t.rich('explainer', {
                br: () =>
                  (
                    <>
                      <br />
                      <br />
                    </>
                  ) as any,
              })}
            </p>
            <p className="rounded-xl flex items-center italic">
              <Icon
                name={Icons.INFORMATION_CIRCLE}
                size={24}
                className="mr-2"
              />
              <span>{t('weth')}</span>
            </p>
          </div>
        </Collapsible>
      </aside>
      <div className="flex justify-between items-center">
        <div className="space-x-4">
          <div className="relative inline-flex ring-1 ring-gray-300 rounded-md p-2 hover:bg-gray-50">
            <select
              ref={selectCollectionRef}
              className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer m-0"
              value={selectedVault?.vaultId ?? ''}
              onChange={handleSelectCollectionChange}
            >
              <option value="">{t('all')}</option>
              {vaults?.map((x) => (
                <option value={x.vaultId} key={x.vaultId}>
                  {x.token.symbol}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`rounded-md flex items-center justify-between cursor-pointer text-base font-medium text-gray-800`}
              onClick={() => handleClick(selectCollectionRef)}
            >
              <span className="flex items-center mr-2">
                {selectedVault?.token.symbol ?? t('all')}
              </span>
              <Icon name={Icons.FUNNEL} size={16} />
            </button>
          </div>
          <div className="relative inline-flex ring-1 ring-gray-300 rounded-md p-2 hover:bg-gray-50">
            <select
              ref={sortRef}
              className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer m-0"
              value={sortDir}
              onChange={handleSortChange}
            >
              <option value="newest">{t('newest')}</option>
              <option value="oldest">{t('oldest')}</option>
            </select>
            <button
              type="button"
              className={`rounded-md flex items-center justify-between cursor-pointer text-base font-medium text-gray-800`}
              onClick={() => handleClick(sortRef)}
            >
              <span className="flex items-center mr-2">{t(sortDir)}</span>
              <Icon name={Icons.CHEVRON_DOWN} size={16} />
            </button>
          </div>
        </div>
        <div className="ml-auto text-sm text-gray-500">
          {isLoading && (
            <Icon
              name={Icons.SPINNER_GAP}
              size={20}
              className={`mr-2 will-change-transform ${
                isLoading ? 'animate-spin' : ''
              }`}
            />
          )}
          {t.rich('refreshed', {
            block: blockNumber?.toString(),
            time: (
              <span className="block text-xs text-right text-gray-500/70">
                {relativeTime(updated, now)}
              </span>
            ) as any,
          })}
        </div>
      </div>
      <div className="px-6 py-8 bg-gray-50 rounded-md">
        {items?.length > 0 ? (
          <>
            <h3 className="font-bold text-lg mb-4">{t('active')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-2 gap-y-4">
              {children}
            </div>
          </>
        ) : (
          <p className="text-center text-gray-500">{t('none')}</p>
        )}
      </div>
    </article>
  );
};

export default Auctions;
